/**
 * CollapsedFormActions Component
 *
 * Actions displayed in the collapsed mobile form toolbar.
 * Shows Cancel + Save/Next buttons based on form validation state.
 *
 * @module components/ui/SlidePanel/CollapsedFormActions
 */

import React from 'react';
import { Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { CollapsedFormActionsProps } from './SlidePanel.types';
import styles from './CollapsedFormActions.module.css';

export const CollapsedFormActions: React.FC<CollapsedFormActionsProps> = ({
  onCancel,
  onSave,
  onExpand,
  canSave,
  isSubmitting,
  saveLabel,
  nextLabel,
}) => {
  const { t } = useTranslation();

  // Save (active) → Save (disabled) → Next (only when nextLabel provided).
  // Forms without a multi-step "Next" affordance (e.g. note form) get a
  // disabled Save instead, mirroring the desktop footer's behavior.
  const renderPrimary = () => {
    if (canSave && onSave) {
      return (
        <Button onClick={onSave} loading={isSubmitting} size="sm">
          {saveLabel}
        </Button>
      );
    }
    if (nextLabel) {
      return (
        <Button onClick={onExpand} disabled={isSubmitting} size="sm">
          {nextLabel}
        </Button>
      );
    }
    return (
      <Button disabled size="sm">
        {saveLabel}
      </Button>
    );
  };

  return (
    <Group className={styles.container} justify="space-between" wrap="nowrap">
      <Button
        variant="subtle"
        onClick={onCancel}
        disabled={isSubmitting}
        size="sm"
      >
        {t('common.cancel')}
      </Button>
      {renderPrimary()}
    </Group>
  );
};
